import React, { useState, useReducer, useEffect } from 'react';
import { Redirect, BrowserRouter as Router, Route, Switch, useLocation } from 'react-router-dom';
import { ApolloProvider } from '@apollo/react-hooks';
import LogRocket from 'logrocket';
import es from 'date-fns/locale/es';
import { registerLocale, setDefaultLocale } from "react-datepicker";

import { register } from './serviceWorker';
import { ProfileQL } from './models/Profile';

import Loading from './controllers/Animations/loadScreen';
import ToasterNotifications from './controllers/visuals/ToasterNotifications';
import MenuGeneral from './controllers/Menu/Menu';
import Support from './controllers/Support/Support';
import GraphQlClient from './services/graphql';
import Register from './controllers/Registration/Register';
import Login from './controllers/Registration/Login';
import RecoverPassword from './controllers/Registration/LostPassword';
import SetupFiscalPop from './controllers/Setup/Setup';
import Dashboard from './controllers/Dashboard/Dashboard';
import SetupSecurityFiscalPop from './controllers/Setup/Setup.security';
import Facturar from './controllers/Facturar/Facturar';
import PreviewFactura from './controllers/FacturaPreview/Preview';
import Pagos from './controllers/Pagos/Pagos';
import MisFacturas from './controllers/MisFacturas/MisFacturas';
import PreviewBillEventFactura from './controllers/FacturaPreview/Preview.billEvents';
import ClientsCatalog from './controllers/Catalogos/Clientes/Clientes';
import ConceptosCatalog from './controllers/Catalogos/Conceptos/Conceptos';
import ErrorsToasts from './controllers/visuals/ErrorToasts';
import CreditNote from './controllers/creditNotes/CreditNote';
import ResetPassword from './controllers/Registration/ResetPassword';
import SetupPagosFiscalPop from './controllers/Setup/Pagos/Setup.Pagos';
import NotificationSetup from './controllers/Support/NotificationSetup';
import RegisterFromLinker from './controllers/Registration/RegisterFromLinker';
import PaymentIntentComplete from './controllers/Setup/Pagos/PaymentIntent.complete';
import CheckoutSuccess from './controllers/Setup/Pagos/Checkout/CheckoutSuccess';
import AccountSubscriptionManager from './controllers/Account/ChangeSubscribe/AccountSubscriptionManager';
import './App.scss';

declare global {
  interface Window { dataLayer: any; gtag: any }
}

registerLocale('es', es)
setDefaultLocale('es');

function currentUserReducer(state: ProfileQL, action: { type: string, payload: ProfileQL }) {
  switch (action.type) {
    case 'set':
      return Object.assign({}, action.payload);
    case 'refresh':
      return Object.assign({}, action.payload);
    default:
      return Object.assign({}, action.payload);
  }
}

function AppLayout({ children, currentUser }: { children: JSX.Element, currentUser: ProfileQL, }) {
  const locationHook = useLocation();
  const hideMenuOn = () => {
    if (locationHook.pathname.includes('/login')) {
      return true;
    }
    if (locationHook.pathname.includes('/register')) {
      return true;
    }
    if (locationHook.pathname.includes('/recover')) {
      return true;
    }
    return false;
  }
  const renderMenuForLoggedIn = (RenderComponent: JSX.Element) => {
    if (hideMenuOn()) {
      return '';
    }
    return !!currentUser && currentUser.professionalProfile ? RenderComponent : '';
  }

  const fpStatus = !!currentUser && !!currentUser.fiscalpopProfileStatus ? currentUser.fiscalpopProfileStatus : null;
  const hasMessage = !!fpStatus && !(fpStatus.modulusMatch && fpStatus.keyModulus && fpStatus.cerModulus);
  const fpTimingErr = !fpStatus ? false : (!!fpStatus.notBefore && (new Date(fpStatus.notBefore).getTime() > Date.now())) || (!!fpStatus.notAfter && (new Date(fpStatus.notAfter).getTime() < Date.now()));

  return (
    <div id="Master" className={`${hasMessage || fpTimingErr ? 'withMessage' : ''} ${(!!currentUser && currentUser.professionalProfile) && !hideMenuOn() ? '' : 'notLogged'}`}>
      <Route path="/" render={(props) => renderMenuForLoggedIn(<MenuGeneral {...props} currentUser={currentUser} />)} />
      {children}
    </div>
  )
}

function App() {

  const [userFetched, setUserFetched] = useState(false);
  const [currentUser, dispatchCurrentUser] = useReducer(currentUserReducer, null as ProfileQL);


  useEffect(() => {
    const userrSubs = GraphQlClient.getCurrentUser()
      .subscribe(user => {
        console.log('[APP] Setting user from token: ', userFetched, user);
        if (!user) {
          // #1 Invoked user returned NULL, user is confirmed not logged in
          setUserFetched(true);
          dispatchCurrentUser({ type: 'set', payload: user });
        } else if (user.professionalProfile && !userFetched) {
          // #2 Invoked user returned a valid user, user is confirmed logged in
          dispatchCurrentUser({ type: 'set', payload: user });
          setUserFetched(true);
          LogRocket.identify(`${user.professionalProfile.email}`, {
            name: `${user.professionalProfile.companyName}`,
            email: user.professionalProfile.email,
            // >> Add your own custom user variables here, ie:
            linker: user.professionalProfile.linkerId,
            hasCfdiUsage: !!user.billing?.stripeSubscriptionCfdiUsage ? 'true' : 'false',
            canBill: !!user.professionalProfile && !!user.professionalProfile.authToken ? `true` : 'false',
            hasDonePayments: !!user.billing && !!user.billing.lastPaymentId ? `true` : 'false'
          });
          // Empty register starts SW without requesting subscription manager
          register();
        } else if (user.professionalProfile && !!userFetched) {
          // #3 Invoked user returned a valid user as subsequent call, user is confirmed logged in
          dispatchCurrentUser({ type: 'refresh', payload: user });
          setUserFetched(true);
        }
      }, (e) => {
        console.error('[APP] Error fetching user: ', e);
        setUserFetched(true);
      })
    return () => {
      userrSubs.unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderLoading = () => {
    return (
      <div id="_AppLoading">
        <Loading display={true} />
      </div>
    )
  }

  const _hasIncomepleteRegister = () => {
    // Caused by Account Spawnner with "Otro RFC"
    return !!currentUser?.professionalProfile && !currentUser?.fiscalpopProfile;
  }

  const renderForIncompleteRegister = (RenderComponent: JSX.Element) => {
    return _hasIncomepleteRegister() ? RenderComponent : <Redirect to="/setup/emisor" />;
  }

  const renderForLoggedOut = (RenderComponent: JSX.Element) => {
    if (_hasIncomepleteRegister()) {
      return <Redirect to="/register-incomplete" />
    }
    return !!currentUser?.professionalProfile ? <Redirect to="/dashboard" /> : RenderComponent;
  }

  const renderForRedirect = (RenderComponent: JSX.Element) => {
    if (_hasIncomepleteRegister()) {
      return <Redirect to="/register-incomplete" />
    }
    return !currentUser?.professionalProfile ? <Redirect to="/login" /> : RenderComponent;
  }

  // Main render is only called once userFetched is true (Response from Back End is received)
  const renderMain = () => {
    return (
      <Router>
        <ToasterNotifications />
        {
          !!currentUser?.professionalProfile ?
          <AccountSubscriptionManager /> : null
        }

        <ErrorsToasts currentUser={currentUser} />
        <AppLayout currentUser={currentUser}>


          <Switch>
            <Route exact path="/" render={(props) => renderForRedirect(
              <Redirect to="/dashboard" {...props} />
            )} />

            {
              /* 
              =================
              MAIN BILLING ROUTES (LITE)
              */
            }
            <Route exact path="/dashboard" render={(props) => renderForRedirect(
              <Dashboard {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/facturar/preview/billevent" render={(props) => renderForRedirect(
              <PreviewBillEventFactura {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/facturar/preview" render={(props) => renderForRedirect(
              <PreviewFactura {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/facturar/preview/:argumentid" render={(props) => renderForRedirect(
              <PreviewFactura {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/facturar/preview/:argumentid/:eventid" render={(props) => renderForRedirect(
              <PreviewFactura {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/setup/pagos" render={(props) => renderForRedirect(
              <Pagos {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/setup/pagos/:eventid" render={(props) => renderForRedirect(
              <Pagos {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/facturar" render={(props) => renderForRedirect(
              <Facturar {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/facturar/:argumentid" render={(props) => renderForRedirect(
              <Facturar {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/facturar/:argumentid/:eventid" render={(props) => renderForRedirect(
              <Facturar {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/notacredito" render={(props) => renderForRedirect(
              <CreditNote {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/facturacion/notacredito/:eventid" render={(props) => renderForRedirect(
              <CreditNote {...props} currentUser={currentUser} />
            )} />

            <Route exact path="/facturas" render={(props) => renderForRedirect(
              <MisFacturas {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/setup/emisor" render={(props) => renderForRedirect(
              <SetupFiscalPop {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/setup/pagos" render={(props) => renderForRedirect(
              <SetupPagosFiscalPop {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/setup/security" render={(props) => renderForRedirect(
              <SetupSecurityFiscalPop {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/support" render={(props) => renderForRedirect(
              <Support {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/setup/notifications" render={(props) => renderForRedirect(
              <NotificationSetup {...props} currentUser={currentUser} />
            )} />

             <Route path="/payment/confirm" render={(props) => renderForRedirect(
              <PaymentIntentComplete {...props} currentUser={currentUser} />
            )} />
            <Route path="/checkout/success" render={(props) => renderForRedirect(
              <CheckoutSuccess {...props} currentUser={currentUser} />
            )} />

            <Route exact path="/catalogos" render={(props) => renderForRedirect(
              <Redirect to="/catalogos/clientes" {...props} />
            )} />
            <Route exact path="/catalogos/clientes" render={(props) => renderForRedirect(
              <ClientsCatalog {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/catalogos/conceptos" render={(props) => renderForRedirect(
              <ConceptosCatalog {...props} currentUser={currentUser} />
            )} />
            <Route path="/register" render={(props) => <Register {...props} />} />
            <Route path="/login" render={(props) => renderForLoggedOut(<Login {...props} />)} />
            <Route path="/recover" render={(props) => renderForLoggedOut(<RecoverPassword {...props} />)} />
            <Route path="/register-incomplete" render={(props) => renderForIncompleteRegister(<RegisterFromLinker {...props} currentUser={currentUser} />)} />

            <Route path="/reset/:token" render={(props) => <ResetPassword {...props} />} />
            <Route path="/setup" render={(props) => <Redirect to="/setup/emisor" {...props} />} />

            <Route path="*" render={(props) => <Redirect to="/" {...props} />} />
          </Switch>
        </AppLayout>
      </Router>
    )
  }

  return (
    <ApolloProvider client={GraphQlClient.client}>
      {
        userFetched ?
          renderMain() :
          renderLoading()
      }
    </ApolloProvider>
  )
}

export default App;
